import { isString } from '../fold-helpers';
import { createWidgetNormalizer } from '../create-widget-normalizer';

const SOCIALS_SYNONYM_KEYS = ['networks', 'items', 'links', 'socialNetworks'] as const;
const APPEARANCE_SYNONYM_KEYS = ['appearance', 'appearanceType'] as const;
const ICON_TYPE_SYNONYM_KEYS = ['iconStyle'] as const;
/** The per-entry keys a model sends flat when it means a single-entry widget. */
const SINGLE_ENTRY_KEYS = ['socialNetwork', 'link', 'name', 'description', 'preventLinkConverting'] as const;

/**
 * Wraps a single entry sent flat into the `socials` array. Button's create tool needs the identical
 * fold for `label` → `buttons: [{ label }]`, and for the same observed reason: asked for an array
 * of objects, a model that only wants ONE of them frequently sends its fields directly instead. A
 * plain `z.object()` strips them SILENTLY, so `create_social_widget` would either hard-fail on a
 * missing `socials` or — worse on update — report success having changed nothing.
 *
 * Runs `after` the rules: it must see whatever the `socials` rule resolved from `networks`/`items`/
 * `links`, since an entry is only wrapped when no array arrived by any name.
 */
function foldSingleEntry(args: Record<string, unknown>, data: Record<string, unknown>): void {
    if (!('socials' in data)) {
        const entry: Record<string, unknown> = {};

        for (const key of SINGLE_ENTRY_KEYS) {
            const value = data[key] ?? args[key];

            if (value !== undefined) {
                entry[key] = value;
            }
        }

        // Only a network or a link makes this look like an entry at all — a stray `name` on its own
        // is more likely noise than a one-item list.
        if (entry['socialNetwork'] !== undefined || entry['link'] !== undefined) {
            data['socials'] = [entry];
        }
    }

    for (const key of SINGLE_ENTRY_KEYS) {
        delete data[key];
    }
}

/**
 * The normalization shared by `create_social_widget` and `update_social_widget` — identical for
 * both, since a social patch and a social create body carry the same field surface (only `socials`'
 * requiredness differs, which the create schema tightens on its own).
 *
 * Beyond the single-entry wrap: `networks`/`items`/`links` for the array itself, `appearance`/
 * `appearanceType` for the shared `type` (the signal form's own accessor is called `appearanceType`,
 * so the name is genuinely ambiguous), `iconStyle` for `iconType`, and the usual alignment words.
 */
const socialNormalizer = createWidgetNormalizer({
    rules: [
        { target: 'socials', synonyms: SOCIALS_SYNONYM_KEYS, accepts: Array.isArray },
        { target: 'type', synonyms: APPEARANCE_SYNONYM_KEYS, accepts: isString },
        { target: 'iconType', synonyms: ICON_TYPE_SYNONYM_KEYS, accepts: isString },
    ],
    after: [foldSingleEntry],
    horizontalAlign: true,
    extraFlatKeys: SINGLE_ENTRY_KEYS,
});

export function normalizeSocialWidgetArgs(raw: unknown): unknown {
    return socialNormalizer.normalize(raw);
}
